// LeaseScenarios — v1.0.0 Phase 3 (Lease-Leverage Module)
//
// Three-card workspace for managing the Conservative / Hybrid / STR
// revenue scenarios on a single lease-leverage lead. Each card holds
// a variable lot list; the expense stack (read-only) comes from the
// lead's lease_terms row.
//
// NOI math is server-side: upsertLeaseScenarios reads expenses from
// lease_terms, sums client-supplied lot_revenues, and writes back
// total_monthly_revenue / monthly_noi / annual_noi. The client-side
// preview is purely cosmetic for live feedback while typing.
//
// Navigation contract: opening this page for a lead that has no
// lease_terms row redirects back to /leads with an errorMessage in
// location.state — /leads reads that and shows a transient banner.
import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { getLeaseTerms } from "../lib/leaseTerms";
import { getLeaseScenarios, upsertLeaseScenarios } from "../lib/leaseScenarios";
import { leaseScenarioSaved, leaseScenarioCompared } from "../lib/leaseScenarioAnalytics";

// ---- scenario metadata ----
const SCENARIO_TYPES = ["conservative", "hybrid", "str"];
const SCENARIO_LABEL = {
  conservative: "Conservative",
  hybrid:       "Hybrid",
  str:          "STR",
};
const SCENARIO_SUBLABEL = {
  conservative: "All long-term rentals. Safe floor.",
  hybrid:       "Mix of LTR + premium positioning.",
  str:          "All three lots short-term. Upside.",
};

// Default lot seeds for empty cards. Forgotten Coast deal numbers from
// Scenario_Model.docx — the canonical first deal is one click away.
const DEFAULT_LOTS = {
  conservative: [
    { name: "Front Lot",  monthly_revenue: 1200 },
    { name: "Back Lot 1", monthly_revenue: 850  },
    { name: "Back Lot 2", monthly_revenue: 850  },
  ],
  hybrid: [
    { name: "Front Lot",  monthly_revenue: 1600 },
    { name: "Back Lot 1", monthly_revenue: 900  },
    { name: "Back Lot 2", monthly_revenue: 1200 },
  ],
  str: [
    { name: "Front Lot",  monthly_revenue: 1425 },
    { name: "Back Lot 1", monthly_revenue: 1425 },
    { name: "Back Lot 2", monthly_revenue: 1425 },
  ],
};

// ---- styles (matches /leads + MyAnalyses) ----
const page = { background: "#f3f4f6", minHeight: "100vh", padding: "40px 20px" };
const container = { maxWidth: 1280, margin: "0 auto" };
const card = {
  background: "#ffffff",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
};
const headerRow = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: 8,
  flexWrap: "wrap",
  gap: 12,
};
const primaryButton = {
  background: "#1d4ed8",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  padding: "10px 18px",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
};
const secondaryButton = {
  background: "#fff",
  color: "#1d4ed8",
  border: "1px solid #93c5fd",
  borderRadius: 12,
  padding: "8px 14px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-block",
};
const subtleButton = {
  background: "transparent",
  color: "#6b7280",
  border: "1px dashed #d1d5db",
  borderRadius: 10,
  padding: "8px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
  marginTop: 6,
};
const lotInput = {
  border: "1px solid #d1d5db",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 14,
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
};
const removeLotButton = {
  background: "#fef2f2",
  color: "#b91c1c",
  border: "1px solid #fecaca",
  borderRadius: 8,
  padding: "4px 8px",
  fontSize: 12,
  cursor: "pointer",
  fontWeight: 700,
};
const sublabel = {
  color: "#6b7280",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 1,
};
const sublabelValue = {
  color: "#111827",
  fontSize: 22,
  fontWeight: 800,
  marginTop: 2,
};

// ---- formatters ----
function usd(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function num(v) {
  return v === "" || v === null || v === undefined ? null : Number(v);
}

// ---- the page ----
export default function LeaseScenarios() {
  const { leadId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  // When set, render <Navigate /> in the return path so React Router
  // performs the redirect deterministically as part of the render tree.
  // Imperative navigate() from inside an async effect was racing with
  // initial render and leaving us stuck on the loading state.
  const [redirectMessage, setRedirectMessage] = useState(null);
  const [leaseTerms, setLeaseTerms] = useState(null);
  // Per-scenario UI state. lots is an array of { name, monthly_revenue }
  // where monthly_revenue is stored as a string so half-typed numerics
  // ("12") don't cause React-controlled-input pain. We coerce on save.
  const [scenarios, setScenarios] = useState({
    conservative: emptyScenarioState(),
    hybrid:       emptyScenarioState(),
    str:          emptyScenarioState(),
  });

  function emptyScenarioState() {
    return {
      lots:      [],   // [{ name, monthly_revenue: "" }]
      server:    null, // last server response: { total_monthly_revenue, monthly_noi, annual_noi }
      saving:    false,
      errorMsg:  "",
    };
  }

  // Stringify revenue from the API (numeric) for the input.
  function hydrateLots(serverLots) {
    if (!Array.isArray(serverLots)) return [];
    return serverLots.map((l) => ({
      name:            String(l.name || ""),
      monthly_revenue: l.monthly_revenue === null || l.monthly_revenue === undefined ? "" : String(l.monthly_revenue),
    }));
  }
  function defaultLotsAsState(type) {
    return DEFAULT_LOTS[type].map((l) => ({
      name:            l.name,
      monthly_revenue: String(l.monthly_revenue),
    }));
  }

  const load = useCallback(async () => {
    setLoading(true);
    setPageError("");

    const { data: termsData, error: termsErr } = await getLeaseTerms(leadId);

    // Two failure modes collapse to the same redirect, because
    // get-lease-terms scopes by (lead_id AND user_id) — so a bogus
    // lead UUID, an unowned lead, and a real lead with no lease_terms
    // row all surface here as `lease_terms: null` with no error.
    if (termsErr || !termsData?.lease_terms) {
      setRedirectMessage(
        "This lead has no lease structure yet. Add it from the New Lead modal or edit the lead first."
      );
      return;
    }
    const lt = termsData.lease_terms;
    setLeaseTerms(lt);

    const { data: scenariosData, error: scenariosErr } = await getLeaseScenarios(lt.id);
    if (scenariosErr) {
      setPageError(scenariosErr.message || "Could not load scenarios.");
      setLoading(false);
      return;
    }
    const byType = Object.fromEntries(
      (scenariosData?.scenarios || []).map((s) => [s.scenario_type, s])
    );

    // Seed each card. Existing row → hydrate. Empty → Forgotten Coast defaults.
    setScenarios({
      conservative: seedCard(byType.conservative, "conservative"),
      hybrid:       seedCard(byType.hybrid,       "hybrid"),
      str:          seedCard(byType.str,          "str"),
    });
    // Customer value — a real ≥2-way lease comparison is realized (M-020),
    // the core lease-leverage value moment. Count of persisted scenarios only.
    const savedCount = Object.keys(byType).length;
    if (savedCount >= 2) leaseScenarioCompared({ scenario_count: savedCount });
    setLoading(false);
  }, [leadId]);

  function seedCard(serverRow, type) {
    if (serverRow) {
      return {
        lots:     hydrateLots(serverRow.lot_revenues),
        server:   {
          total_monthly_revenue: Number(serverRow.total_monthly_revenue),
          monthly_noi:           Number(serverRow.monthly_noi),
          annual_noi:            Number(serverRow.annual_noi),
        },
        saving:   false,
        errorMsg: "",
      };
    }
    return {
      lots:     defaultLotsAsState(type),
      server:   null,
      saving:   false,
      errorMsg: "",
    };
  }

  useEffect(() => { load(); }, [load]);

  // ---- expense stack from lease_terms (read-only, drives client preview) ----
  const expenseStack = leaseTerms
    ? Number(leaseTerms.monthly_expense_lease_payment   || 0) +
      Number(leaseTerms.monthly_expense_utilities       || 0) +
      Number(leaseTerms.monthly_expense_maintenance     || 0) +
      Number(leaseTerms.monthly_expense_vacancy_reserve || 0) +
      Number(leaseTerms.monthly_expense_insurance       || 0)
    : 0;

  // Client preview of totals for a card (purely cosmetic — server is
  // authoritative on save).
  function previewTotals(lots) {
    const total = lots.reduce((acc, l) => acc + (Number(l.monthly_revenue) || 0), 0);
    const monthlyNoi = total - expenseStack;
    return {
      total_monthly_revenue: total,
      monthly_noi:           monthlyNoi,
      annual_noi:            monthlyNoi * 12,
    };
  }

  // ---- lot mutations ----
  function setLot(type, idx, key, value) {
    setScenarios((prev) => {
      const lots = prev[type].lots.slice();
      lots[idx] = { ...lots[idx], [key]: value };
      return { ...prev, [type]: { ...prev[type], lots } };
    });
  }
  function addLot(type) {
    setScenarios((prev) => ({
      ...prev,
      [type]: { ...prev[type], lots: [...prev[type].lots, { name: "", monthly_revenue: "" }] },
    }));
  }
  function removeLot(type, idx) {
    setScenarios((prev) => {
      const lots = prev[type].lots.slice();
      lots.splice(idx, 1);
      return { ...prev, [type]: { ...prev[type], lots } };
    });
  }

  // ---- save one scenario ----
  async function handleSave(type) {
    const card = scenarios[type];
    const lots = card.lots;
    // Customer value — is this a create or an edit? (upsert; server null → new.)
    const isNew = !card.server;
    // Client-side validation that mirrors the server's 400 paths so the
    // user gets a friendly message before the round-trip.
    if (lots.length === 0) {
      return setCardError(type, "Add at least one lot before saving.");
    }
    for (let i = 0; i < lots.length; i++) {
      if (!lots[i].name || !lots[i].name.trim()) {
        return setCardError(type, `Lot ${i + 1} needs a name.`);
      }
      const r = num(lots[i].monthly_revenue);
      if (r === null || !Number.isFinite(r)) {
        return setCardError(type, `Lot ${i + 1} (${lots[i].name}) needs a numeric monthly revenue.`);
      }
    }

    setScenarios((prev) => ({
      ...prev,
      [type]: { ...prev[type], saving: true, errorMsg: "" },
    }));

    const payload = [
      {
        scenario_type: type,
        lot_revenues:  lots.map((l) => ({
          name:            l.name.trim(),
          monthly_revenue: Number(l.monthly_revenue),
        })),
      },
    ];
    const { data, error } = await upsertLeaseScenarios(leaseTerms.id, payload);

    if (error) {
      setScenarios((prev) => ({
        ...prev,
        [type]: { ...prev[type], saving: false, errorMsg: error.message || "Save failed." },
      }));
      return;
    }
    // Server returns the upserted set sorted; find the one we just wrote.
    const saved = (data?.scenarios || []).find((s) => s.scenario_type === type);
    setScenarios((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        lots:     hydrateLots(saved?.lot_revenues || lots),
        server:   saved
          ? {
              total_monthly_revenue: Number(saved.total_monthly_revenue),
              monthly_noi:           Number(saved.monthly_noi),
              annual_noi:            Number(saved.annual_noi),
            }
          : null,
        saving:   false,
        errorMsg: "",
      },
    }));
    // Customer value — scenario create-or-edit (M-019). Enum + flag only.
    leaseScenarioSaved({ scenario_type: type, is_new: isNew });
  }
  function setCardError(type, msg) {
    setScenarios((prev) => ({
      ...prev,
      [type]: { ...prev[type], errorMsg: msg },
    }));
  }

  // ---- render ----
  // Declarative redirect: React Router unmounts this component and mounts
  // /leads with errorMessage in location.state. Leads.js useEffect reads
  // that state, surfaces it in the existing red error banner, and clears
  // history state so a refresh doesn't re-show it.
  if (redirectMessage) {
    return (
      <Navigate to="/leads" replace state={{ errorMessage: redirectMessage }} />
    );
  }

  if (loading || !leaseTerms) {
    return (
      <div style={page}>
        <div style={container}>
          <p style={{ color: "#6b7280", fontSize: 16 }}>Loading scenarios…</p>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div style={page}>
        <div style={container}>
          <div
            style={{
              ...card,
              border: "2px solid #ef4444",
              background: "#fff5f5",
              color: "#b91c1c",
              fontWeight: 600,
            }}
          >
            {pageError}
          </div>
          <button type="button" style={{ ...secondaryButton, marginTop: 16 }} onClick={() => navigate("/leads")}>
            ← Back to Leads
          </button>
        </div>
      </div>
    );
  }

  const addrDisplay =
    [leaseTerms?.address, leaseTerms?.city, leaseTerms?.state, leaseTerms?.zip].filter(Boolean).join(", ") || "this lead";

  return (
    <div style={page}>
      <div style={container}>
        {/* Header */}
        <div style={{ ...headerRow, marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 800, color: "#111827", margin: "0 0 4px" }}>
              Scenarios
            </h1>
            <p style={{ color: "#6b7280", fontSize: 15, margin: 0 }}>
              Three revenue scenarios for one lease-leverage deal.
            </p>
          </div>
          <button type="button" style={secondaryButton} onClick={() => navigate("/leads")}>
            ← Back to Leads
          </button>
        </div>

        {/* Lease summary */}
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Lease structure</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 10 }}>
            {usd(leaseTerms.base_rent_monthly)}/mo base · {leaseTerms.lease_term_months}-month term · option {leaseTerms.option_window_start_month}–{leaseTerms.option_window_end_month}
          </div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
            <strong>Monthly expense stack: {usd(expenseStack)}</strong>
            {" "}— lease {usd(leaseTerms.monthly_expense_lease_payment)} +
            {" "}utils {usd(leaseTerms.monthly_expense_utilities)} +
            {" "}maint {usd(leaseTerms.monthly_expense_maintenance)} +
            {" "}vac {usd(leaseTerms.monthly_expense_vacancy_reserve)} +
            {" "}ins {usd(leaseTerms.monthly_expense_insurance)}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>
            Property: {addrDisplay}
          </div>
        </div>

        {/* 3-card grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 18,
          }}
        >
          {SCENARIO_TYPES.map((type) => (
            <ScenarioCard
              key={type}
              type={type}
              card={scenarios[type]}
              expenseStack={expenseStack}
              preview={previewTotals(scenarios[type].lots)}
              onSetLot={(idx, key, value) => setLot(type, idx, key, value)}
              onAddLot={() => addLot(type)}
              onRemoveLot={(idx) => removeLot(type, idx)}
              onSave={() => handleSave(type)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- ScenarioCard ----
function ScenarioCard({ type, card, expenseStack, preview, onSetLot, onAddLot, onRemoveLot, onSave }) {
  // Show server values when they exist and match the current lot fingerprint
  // (server is authoritative). Otherwise show the live client preview, which
  // is clearly labeled so the user knows it's not yet saved.
  const showServerValues = !!card.server && isLotsClean(card.lots, card.server);

  return (
    <div style={card.errorMsg ? { ...cardWithBorder("#ef4444") } : cardStyle}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: scenarioAccent(type), textTransform: "uppercase", letterSpacing: 1 }}>
          {SCENARIO_LABEL[type]}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
          {SCENARIO_SUBLABEL[type]}
        </div>
      </div>

      {/* Lots */}
      <div style={{ marginBottom: 14 }}>
        {card.lots.map((lot, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 110px 36px", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input
              style={lotInput}
              value={lot.name}
              onChange={(e) => onSetLot(idx, "name", e.target.value)}
              placeholder="Lot name"
            />
            <input
              style={{ ...lotInput, textAlign: "right" }}
              value={lot.monthly_revenue}
              onChange={(e) => onSetLot(idx, "monthly_revenue", e.target.value)}
              inputMode="numeric"
              placeholder="$/mo"
            />
            <button
              type="button"
              onClick={() => onRemoveLot(idx)}
              style={removeLotButton}
              aria-label={`Remove lot ${idx + 1}`}
              title="Remove this lot"
            >
              ✕
            </button>
          </div>
        ))}
        <button type="button" onClick={onAddLot} style={subtleButton}>
          + Add lot
        </button>
      </div>

      {/* Totals (server values or client preview) */}
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12, marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <div>
            <div style={sublabel}>Revenue</div>
            <div style={sublabelValue}>{usd(showServerValues ? card.server.total_monthly_revenue : preview.total_monthly_revenue)}</div>
          </div>
          <div>
            <div style={sublabel}>Monthly NOI</div>
            <div style={{ ...sublabelValue, color: (showServerValues ? card.server.monthly_noi : preview.monthly_noi) >= 0 ? "#15803d" : "#b91c1c" }}>
              {usd(showServerValues ? card.server.monthly_noi : preview.monthly_noi)}
            </div>
          </div>
          <div>
            <div style={sublabel}>Annual NOI</div>
            <div style={{ ...sublabelValue, color: (showServerValues ? card.server.annual_noi : preview.annual_noi) >= 0 ? "#15803d" : "#b91c1c" }}>
              {usd(showServerValues ? card.server.annual_noi : preview.annual_noi)}
            </div>
          </div>
        </div>
        {!showServerValues && (
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, fontStyle: "italic" }}>
            Preview — click Save to compute on the server.
          </div>
        )}
      </div>

      {card.errorMsg && (
        <div style={{ color: "#b91c1c", fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          {card.errorMsg}
        </div>
      )}

      <button
        type="button"
        onClick={onSave}
        disabled={card.saving}
        style={{
          ...primaryButton,
          width: "100%",
          opacity: card.saving ? 0.6 : 1,
          cursor: card.saving ? "not-allowed" : "pointer",
        }}
      >
        {card.saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// Returns true if the current input lots match what the server last computed
// (i.e. no unsaved edits). Used to decide whether to show server NOI or the
// live preview.
function isLotsClean(currentLots, serverPreview) {
  if (!serverPreview) return false;
  const liveTotal = currentLots.reduce((acc, l) => acc + (Number(l.monthly_revenue) || 0), 0);
  return liveTotal === Number(serverPreview.total_monthly_revenue);
}

function scenarioAccent(type) {
  return { conservative: "#374151", hybrid: "#1d4ed8", str: "#15803d" }[type] || "#374151";
}

const cardStyle = {
  background: "#ffffff",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  border: "1px solid transparent",
};
function cardWithBorder(color) {
  return { ...cardStyle, border: `2px solid ${color}`, background: "#fff" };
}
